import { useState, useEffect, useMemo, useRef } from 'react';
import { MdSearch, MdReceiptLong, MdInfoOutline, MdCalendarToday, MdExpandMore, MdChevronLeft, MdChevronRight, MdAdd, MdFileDownload } from 'react-icons/md';
import { getLedger, voidOtherIncome } from '../../services/financeService';
import { downloadLedgerReport } from '../../services/reportExportService';
import DualDate from '../../components/common/DualDate';
import DualDatePicker from '../../components/common/DualDatePicker';
import RecordIncomeModal from '../../components/admin/RecordIncomeModal';
import useAuth from '../../hooks/useAuth';
import { adToBs, BS_MONTHS } from '../../utils/calendar';

const fmtNPR = (n) => `NPR ${Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
const fmtSigned = (n) => (n < 0 ? `− ${fmtNPR(Math.abs(n))}` : fmtNPR(n));
const fmtBal = (n) => (n == null ? '—' : fmtNPR(n)); // null = unavailable (before the anchor)

const ACCOUNT_OPTIONS = ['All', 'Cash', 'Bank', 'eWallet', 'Unclassified'];
const ACCOUNT_KEYS = [['cash', 'Cash'], ['bank', 'Bank'], ['ewallet', 'eWallet'], ['unclassified', 'Unclassified']];
const pad2 = (n) => String(n).padStart(2, '0');
const daysInMonth = (y, m) => new Date(Date.UTC(y, m, 0)).getUTCDate();

const METHOD_LABELS = {
  cash: 'Cash', bank: 'Bank Transfer', bank_transfer: 'Bank Transfer', connectips: 'connectIPS',
  khalti: 'Khalti', esewa: 'eSewa', fonepay: 'Fonepay', fonepay_qr: 'Fonepay', other: 'Other',
};
const methodLabel = (m) => METHOD_LABELS[m] || (m ? m.charAt(0).toUpperCase() + m.slice(1) : '—');

const parseLocal = (iso) => { const [y, m, d] = String(iso).split('-').map(Number); return new Date(y, (m || 1) - 1, d || 1); };

function bsRangeLabel(startIso, endIso) {
  try {
    const s = adToBs(parseLocal(startIso));
    const e = adToBs(parseLocal(endIso));
    const sM = BS_MONTHS[s.month - 1], eM = BS_MONTHS[e.month - 1];
    return s.year === e.year
      ? `${sM} ${s.day} – ${eM} ${e.day}, ${s.year}`
      : `${sM} ${s.day}, ${s.year} – ${eM} ${e.day}, ${e.year}`;
  } catch { return `${startIso} – ${endIso}`; }
}
// A single KTM day key "YYYY-MM-DD" → short BS label "1 Bhadra 2083" (for tooltips).
function bsDateLabel(iso) {
  try { const bs = adToBs(parseLocal(iso)); return `${bs.day} ${BS_MONTHS[bs.month - 1]} ${bs.year}`; }
  catch { return iso; }
}
// "YYYY-MM" → "Asar 2083" (BS label of that AD month, mid-month sample).
function bsMonthLabel(ym) {
  try { const [y, m] = ym.split('-').map(Number); const bs = adToBs(new Date(y, m - 1, 15)); return `${BS_MONTHS[bs.month - 1]} ${bs.year}`; }
  catch { return ym; }
}

function kthNowYM() {
  const k = new Date(Date.now() + (5 * 60 + 45) * 60 * 1000);
  return { y: k.getUTCFullYear(), m: k.getUTCMonth() + 1 };
}
function presetRange(preset) {
  const { y, m } = kthNowYM();
  if (preset === 'lastMonth') {
    const pm = m === 1 ? 12 : m - 1, py = m === 1 ? y - 1 : y;
    return { start: `${py}-${pad2(pm)}-01`, end: `${py}-${pad2(pm)}-${pad2(daysInMonth(py, pm))}` };
  }
  if (preset === 'thisYear') return { start: `${y}-01-01`, end: `${y}-12-31` };
  return { start: `${y}-${pad2(m)}-01`, end: `${y}-${pad2(m)}-${pad2(daysInMonth(y, m))}` };
}
const PRESETS = [['thisMonth', 'This Month'], ['lastMonth', 'Last Month'], ['thisYear', 'This Year']];

function AccountPill({ account }) {
  const tone = account === 'Unclassified' ? 'bg-nav-hover text-muted' : 'bg-nav-hover text-secondary';
  return <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-medium ${tone}`}>{account}</span>;
}

function RangeControl({ range, onChange }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [open]);

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button" onClick={() => setOpen(o => !o)} aria-haspopup="dialog" aria-expanded={open}
        className="h-9 inline-flex items-center gap-2 px-3 rounded-lg border border-divider bg-card text-sm text-secondary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 transition-colors"
      >
        <MdCalendarToday className="text-muted text-base flex-shrink-0" />
        <span className="font-medium">{bsRangeLabel(range.start, range.end)}</span>
        <MdExpandMore className={`text-muted text-lg flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div role="dialog" className="absolute top-full left-0 mt-1 z-50 w-[320px] max-w-[calc(100vw-2rem)] bg-card border border-divider rounded-xl shadow-xl p-4 max-h-[70vh] overflow-y-auto">
          <DualDatePicker label="From" value={range.start} max={range.end} onChange={(v) => v && onChange({ ...range, start: v })} />
          <div className="mt-4 pt-4 border-t border-divider">
            <DualDatePicker label="To" value={range.end} min={range.start} onChange={(v) => v && onChange({ ...range, end: v })} />
          </div>
          <div className="flex justify-end mt-3 pt-3 border-t border-divider">
            <button type="button" onClick={() => setOpen(false)} className="h-8 px-4 rounded-lg bg-primary text-white text-sm font-medium">Done</button>
          </div>
        </div>
      )}
    </div>
  );
}

const fmtPlain = (n) => Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 0 });

// Compact per-account balance card. Cash is emphasised (it answers "cash at reception now").
// When the balance is anchored, a muted breakdown explains WHY "Started at" exceeds what the owner
// typed: it's their counted amount plus whatever was collected/spent since their start date.
function BalanceCard({ account, emphasise, seed, asOf }) {
  const anchored = asOf && account.openingAvailable && account.opening != null;
  const since = anchored ? account.opening - (seed || 0) : 0;
  const showBreakdown = anchored && since !== 0;
  const breakdown = since >= 0
    ? `You counted ${fmtPlain(seed)} · ${fmtPlain(since)} collected since then`
    : `You counted ${fmtPlain(seed)} · ${fmtPlain(-since)} spent since then`;

  return (
    <div className={`flex-1 min-w-[150px] bg-card rounded-xl border p-3 ${emphasise ? 'border-primary/40 ring-1 ring-primary/20' : 'border-divider'}`}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold text-secondary">{account.account}</span>
        {!account.openingAvailable && <span className="text-[10px] text-muted">unanchored</span>}
      </div>
      <p className={`text-lg font-bold mt-1 ${account.closing == null ? 'text-muted' : 'text-secondary'}`}>{fmtBal(account.closing)}</p>
      <p className="text-[11px] text-muted mt-1">Started at {fmtBal(account.opening)}</p>
      {showBreakdown && (
        <p className="text-[11px] text-subtle leading-snug" title={`Counted on ${bsDateLabel(asOf)}`}>{breakdown}</p>
      )}
      <div className="flex items-center gap-2 mt-1 text-[11px]">
        <span className="text-green-600">+{fmtNPR(account.in)}</span>
        <span className="text-red-600">−{fmtNPR(account.out)}</span>
      </div>
    </div>
  );
}

export default function FinanceLedger() {
  const { user } = useAuth();
  const isPrimary = user?.adminType === 'primary' || user?.isPrimaryAdmin === true;

  const [range, setRange] = useState(() => presetRange('thisMonth'));
  const [activePreset, setActivePreset] = useState('thisMonth');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshTick, setRefreshTick] = useState(0); // bump to refetch after record/void

  const [view, setView] = useState('transactions'); // 'transactions' | 'daily'
  const [showRecord, setShowRecord] = useState(false);
  const [voidTarget, setVoidTarget] = useState(null); // ledger row to void
  const [voidReason, setVoidReason] = useState('');
  const [voiding, setVoiding] = useState(false);

  // Transaction filters
  const [account, setAccount] = useState('All');
  const [category, setCategory] = useState('All');
  const [direction, setDirection] = useState('all');
  const [search, setSearch] = useState('');
  const [exporting, setExporting] = useState(false);

  // Daily view controls
  const [dailyAccount, setDailyAccount] = useState('cash');
  const [dailyMonth, setDailyMonth] = useState(null); // 'YYYY-MM'

  useEffect(() => {
    let alive = true;
    setLoading(true); setError('');
    getLedger(range)
      .then(res => { if (alive) setData(res.data); })
      .catch(err => { if (alive) setError(err?.response?.data?.message || 'Failed to load ledger'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [range.start, range.end, refreshTick]);

  const applyPreset = (preset) => { setActivePreset(preset); setRange(presetRange(preset)); };
  const handleRangeChange = (next) => { setActivePreset('custom'); setRange(next); };
  const reload = () => setRefreshTick(t => t + 1);

  const confirmVoid = async () => {
    if (!voidTarget || !voidReason.trim()) return;
    setVoiding(true);
    try {
      await voidOtherIncome(voidTarget.sourceId, voidReason.trim());
      setVoidTarget(null); setVoidReason('');
      reload();
    } catch (err) {
      setError(err?.response?.data?.message || 'Could not void this record.');
    } finally {
      setVoiding(false);
    }
  };
  const onVoid = (row) => { setVoidReason(''); setVoidTarget(row); };

  // Same range + filters the on-screen table is currently showing, so the download matches.
  const handleExport = (format) => {
    downloadLedgerReport({ format, start: range.start, end: range.end, account, category, direction, search }, setExporting)
      .catch(() => {}); // toast already shown by safeDownload
  };

  const allRows = data?.rows || [];
  const daily = data?.daily || [];
  const balances = data?.balances || null;

  // Months present in the daily data (for paging the daily grid when the range spans >1 month).
  const dailyMonths = useMemo(() => [...new Set(daily.map(d => d.date.slice(0, 7)))].sort(), [daily]);
  const multiMonth = dailyMonths.length > 1;
  // Default the daily grid to the MOST RECENT month within the range.
  useEffect(() => {
    if (dailyMonths.length) setDailyMonth(dailyMonths[dailyMonths.length - 1]);
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  const categories = useMemo(() => {
    const set = new Set(allRows.map(r => r.category).filter(Boolean));
    return ['All', ...[...set].sort((a, b) => a.localeCompare(b))];
  }, [allRows]);

  const visibleRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    let out = allRows.filter(r => {
      if (account !== 'All' && r.account !== account) return false;
      if (category !== 'All' && r.category !== category) return false;
      if (direction === 'in' && r.direction !== 'in') return false;
      if (direction === 'out' && r.direction !== 'out') return false;
      if (q) {
        const hay = `${r.description} ${r.subDescription} ${r.studentName} ${r.txnId}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    let run = 0;
    out = out.map(r => { run += r.moneyIn - r.moneyOut; return { ...r, visibleBalance: run }; });
    return out;
  }, [allRows, account, category, direction, search]);

  const isFiltered = account !== 'All' || category !== 'All' || direction !== 'all' || search.trim() !== '';
  const totals = data?.totals || { moneyIn: 0, moneyOut: 0, net: 0, count: 0 };

  // Daily rows for the selected account + month.
  const dailyRows = useMemo(() => {
    const inMonth = multiMonth && dailyMonth ? daily.filter(d => d.date.slice(0, 7) === dailyMonth) : daily;
    return inMonth.map(d => {
      const cell = dailyAccount === 'all' ? d.total : d.accounts[dailyAccount];
      return { date: d.date, ...(cell || { opening: null, in: 0, out: 0, closing: null, available: false }) };
    });
  }, [daily, dailyAccount, dailyMonth, multiMonth]);

  const selectCls = 'h-9 px-2.5 rounded-lg border border-divider bg-card text-sm text-secondary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30';
  const reconcileNote = 'Money in = fees collected + other income (matches the Overview “Collected”). Money out = approved expenses, excluding the TrainAxis subscription.';

  return (
    <div>
      {/* Range control + preset pills */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <RangeControl range={range} onChange={handleRangeChange} />
        <div className="flex items-center gap-1.5 flex-wrap">
          {PRESETS.map(([id, lbl]) => (
            <button
              key={id} type="button" onClick={() => applyPreset(id)}
              className={`h-9 px-3 rounded-full text-xs font-medium transition-colors border ${
                activePreset === id ? 'bg-primary text-white border-primary' : 'border-divider text-muted hover:text-secondary'
              }`}
            >{lbl}</button>
          ))}
          <span className={`h-9 px-3 inline-flex items-center rounded-full text-xs font-medium border ${
            activePreset === 'custom' ? 'bg-primary text-white border-primary' : 'border-divider text-subtle'
          }`}>Custom</span>
        </div>
        <button
          type="button" onClick={() => setShowRecord(true)}
          className="ml-auto h-9 px-3 inline-flex items-center gap-1.5 rounded-lg bg-primary text-white text-sm font-medium hover:opacity-90 transition"
        >
          <MdAdd className="text-base" /> Record income
        </button>
      </div>

      {/* Per-account balance cards (closing emphasised; Cash highlighted) */}
      {balances && !loading && (
        <div className="mb-2">
          <div className="flex flex-wrap gap-2">
            {balances.accounts.map(a => (
              <BalanceCard key={a.key} account={a} emphasise={a.key === 'cash'} seed={balances.seed?.[a.key]} asOf={balances.asOf} />
            ))}
          </div>
          <p className="text-xs text-muted mt-1.5">
            {balances.asOf
              ? <>Based on the money you counted on <DualDate date={parseLocal(balances.asOf)} className="text-muted" />.</>
              : <>No starting money set — balances count up from zero. Add it in Settings → Fees &amp; Billing.</>}
          </p>
        </div>
      )}

      {/* View toggle */}
      <div className="inline-flex items-center border border-divider rounded-lg overflow-hidden my-3">
        {[['transactions', 'Transactions'], ['daily', 'Daily balances']].map(([id, lbl]) => (
          <button
            key={id} type="button" onClick={() => setView(id)}
            className={`px-4 h-9 text-sm font-medium transition-colors ${view === id ? 'bg-primary text-white' : 'text-muted hover:text-secondary'}`}
          >{lbl}</button>
        ))}
      </div>

      {loading ? (
        <div className="space-y-1.5">{[...Array(8)].map((_, i) => <div key={i} className="h-11 bg-nav-hover rounded-lg animate-pulse" />)}</div>
      ) : error ? (
        <div className="bg-card rounded-2xl border border-divider p-8 text-center text-muted">{error}</div>
      ) : view === 'transactions' ? (
        <TransactionsView
          totals={totals} reconcileNote={reconcileNote} selectCls={selectCls}
          account={account} setAccount={setAccount} category={category} setCategory={setCategory}
          categories={categories} direction={direction} setDirection={setDirection}
          search={search} setSearch={setSearch} visibleRows={visibleRows} allRows={allRows} isFiltered={isFiltered}
          isPrimary={isPrimary} onVoid={onVoid} onExport={handleExport} exporting={exporting}
        />
      ) : (
        <DailyView
          rows={dailyRows} dailyAccount={dailyAccount} setDailyAccount={setDailyAccount}
          multiMonth={multiMonth} months={dailyMonths} dailyMonth={dailyMonth} setDailyMonth={setDailyMonth}
          selectCls={selectCls}
        />
      )}

      {showRecord && <RecordIncomeModal onClose={() => setShowRecord(false)} onSaved={reload} />}

      {voidTarget && (
        <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/40 p-0 md:p-4" onMouseDown={() => setVoidTarget(null)}>
          <div className="bg-card w-full md:max-w-sm rounded-t-2xl md:rounded-2xl border border-divider shadow-xl p-4" onMouseDown={e => e.stopPropagation()}>
            <h3 className="text-sm font-semibold text-secondary mb-1">Void this income</h3>
            <p className="text-xs text-subtle mb-3">
              {voidTarget.subDescription || voidTarget.description} · {fmtNPR(voidTarget.moneyIn)}. This can&apos;t be undone —
              the record stays marked void for the audit trail. Enter a new record if you need to re-enter it.
            </p>
            <textarea
              value={voidReason} onChange={e => setVoidReason(e.target.value)} rows={2}
              placeholder="Reason (required) — e.g. entered twice, wrong amount"
              className="w-full rounded-xl border border-divider bg-card text-secondary text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
            <div className="flex items-center justify-end gap-2 mt-3">
              <button type="button" onClick={() => setVoidTarget(null)} className="min-h-[40px] px-4 rounded-full text-sm font-medium text-muted hover:text-secondary">Cancel</button>
              <button type="button" onClick={confirmVoid} disabled={voiding || !voidReason.trim()} className="min-h-[40px] px-5 rounded-full bg-red-600 text-white text-sm font-medium hover:opacity-90 transition disabled:opacity-50">
                {voiding ? 'Voiding…' : 'Void income'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Transactions view (Stage 1 table + summary strip + filters) ──
function TransactionsView({
  totals, reconcileNote, selectCls, account, setAccount, category, setCategory, categories,
  direction, setDirection, search, setSearch, visibleRows, allRows, isFiltered, isPrimary, onVoid,
  onExport, exporting,
}) {
  return (
    <>
      <div className="bg-card rounded-xl border border-divider px-4 py-2.5 mb-4 flex flex-wrap items-center gap-x-6 gap-y-1">
        <div className="flex items-baseline gap-2"><span className="text-xs text-muted">Money In</span><span className="text-sm font-bold text-green-600">{fmtNPR(totals.moneyIn)}</span></div>
        <div className="flex items-baseline gap-2"><span className="text-xs text-muted">Money Out</span><span className="text-sm font-bold text-red-600">{fmtNPR(totals.moneyOut)}</span></div>
        <div className="flex items-baseline gap-2"><span className="text-xs text-muted">Net</span><span className={`text-sm font-bold ${totals.net < 0 ? 'text-red-600' : 'text-secondary'}`}>{fmtSigned(totals.net)}</span></div>
        <span className="text-muted cursor-help" title={reconcileNote} aria-label={reconcileNote}><MdInfoOutline className="text-base" /></span>
        <div className="ml-auto flex items-center gap-2">
          <button type="button" disabled={exporting} onClick={() => onExport('csv')}
            className="h-8 px-3 inline-flex items-center gap-1.5 rounded-lg border border-divider text-xs font-medium text-secondary hover:bg-nav-hover transition-colors disabled:opacity-50">
            <MdFileDownload className="text-base" /> CSV
          </button>
          <button type="button" disabled={exporting} onClick={() => onExport('pdf')}
            className="h-8 px-3 inline-flex items-center gap-1.5 rounded-lg border border-divider text-xs font-medium text-secondary hover:bg-nav-hover transition-colors disabled:opacity-50">
            <MdFileDownload className="text-base" /> PDF
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-3">
        <select value={account} onChange={e => setAccount(e.target.value)} className={selectCls} aria-label="Account">
          {ACCOUNT_OPTIONS.map(a => <option key={a} value={a}>{a === 'All' ? 'All accounts' : a}</option>)}
        </select>
        <select value={category} onChange={e => setCategory(e.target.value)} className={selectCls} aria-label="Category">
          {categories.map(c => <option key={c} value={c}>{c === 'All' ? 'All categories' : c}</option>)}
        </select>
        <div className="inline-flex items-center border border-divider rounded-lg overflow-hidden">
          {[['all', 'All'], ['in', 'In'], ['out', 'Out']].map(([id, lbl]) => (
            <button key={id} type="button" onClick={() => setDirection(id)}
              className={`px-3 h-9 text-sm font-medium transition-colors ${direction === id ? 'bg-primary text-white' : 'text-muted hover:text-secondary'}`}>{lbl}</button>
          ))}
        </div>
        <div className="relative flex-1 min-w-[160px]">
          <MdSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted text-lg" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search description or student…"
            className="w-full h-9 pl-9 pr-3 rounded-lg border border-divider bg-card text-sm text-secondary placeholder:text-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30" />
        </div>
      </div>

      {visibleRows.length === 0 ? (
        <div className="bg-card rounded-2xl border border-divider p-10 text-center">
          <MdReceiptLong className="text-4xl text-muted mx-auto mb-2" />
          <p className="text-sm text-muted">{allRows.length === 0 ? 'No transactions in this period.' : 'No transactions match these filters.'}</p>
        </div>
      ) : (
        <>
          <div className="space-y-2 md:hidden">
            {visibleRows.map((r, i) => (
              <div key={`${r.txnId}-${i}`} className="bg-card rounded-xl border border-divider p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-secondary truncate">{r.description}</p>
                    {r.subDescription && <p className="text-xs text-muted truncate">{r.subDescription}</p>}
                  </div>
                  <p className={`text-sm font-bold flex-shrink-0 ${r.moneyIn ? 'text-green-600' : 'text-red-600'}`}>
                    {r.moneyIn ? `+ ${fmtNPR(r.moneyIn)}` : `− ${fmtNPR(r.moneyOut)}`}
                  </p>
                </div>
                <div className="flex items-center justify-between gap-2 mt-2">
                  <DualDate date={r.date} className="text-xs text-muted" />
                  <div className="flex items-center gap-2"><AccountPill account={r.account} /><span className="text-xs text-muted">{methodLabel(r.method)}</span></div>
                </div>
                <div className="flex items-center justify-between gap-2 mt-1.5 pt-1.5 border-t border-divider">
                  <span className="text-[11px] text-subtle font-mono">{r.txnId}</span>
                  <div className="flex items-center gap-3">
                    {isPrimary && r.sourceType === 'other_income' && (
                      <button type="button" onClick={() => onVoid(r)} className="text-[11px] text-red-600 font-medium hover:underline">Void</button>
                    )}
                    <span className="text-xs text-muted">Bal {r.visibleBalance == null ? '—' : fmtSigned(r.visibleBalance)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="hidden md:block bg-card rounded-2xl border border-divider overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-muted border-b border-divider bg-nav-hover/30">
                    <th className="px-4 py-2 font-semibold">Date</th><th className="px-4 py-2 font-semibold">Txn ID</th>
                    <th className="px-4 py-2 font-semibold">Description</th><th className="px-4 py-2 font-semibold">Category</th>
                    <th className="px-4 py-2 font-semibold text-right">Money In</th><th className="px-4 py-2 font-semibold text-right">Money Out</th>
                    <th className="px-4 py-2 font-semibold">Account</th><th className="px-4 py-2 font-semibold text-right">Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleRows.map((r, i) => (
                    <tr key={`${r.txnId}-${i}`} className="border-b border-divider last:border-0 hover:bg-nav-hover/40 transition-colors">
                      <td className="px-4 py-2 whitespace-nowrap"><DualDate date={r.date} className="text-xs" /></td>
                      <td className="px-4 py-2 whitespace-nowrap text-[11px] font-mono text-muted">
                        {r.txnId}
                        {isPrimary && r.sourceType === 'other_income' && (
                          <button type="button" onClick={() => onVoid(r)} className="ml-2 text-red-600 hover:underline">Void</button>
                        )}
                      </td>
                      <td className="px-4 py-2">
                        <p className="text-secondary font-medium truncate max-w-[200px]">{r.description}</p>
                        {r.subDescription && <p className="text-xs text-muted truncate max-w-[200px]">{r.subDescription}</p>}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-muted">{r.category}</td>
                      <td className="px-4 py-2 text-right whitespace-nowrap font-semibold text-green-600">{r.moneyIn ? fmtNPR(r.moneyIn) : ''}</td>
                      <td className="px-4 py-2 text-right whitespace-nowrap font-semibold text-red-600">{r.moneyOut ? fmtNPR(r.moneyOut) : ''}</td>
                      <td className="px-4 py-2 whitespace-nowrap">
                        <AccountPill account={r.account} />
                        <span className="block text-[11px] text-muted mt-0.5">{methodLabel(r.method)}</span>
                      </td>
                      <td className="px-4 py-2 text-right whitespace-nowrap font-medium text-secondary">{r.visibleBalance == null ? '—' : fmtSigned(r.visibleBalance)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {isFiltered && (
            <p className="text-xs text-muted mt-3">
              Showing {visibleRows.length} of {allRows.length} transactions. Balance column runs over the
              filtered rows; the summary above always reflects the full period.
            </p>
          )}
        </>
      )}
    </>
  );
}

// ── Daily balances view (per account, month-paged when the range spans >1 month) ──
function DailyView({ rows, dailyAccount, setDailyAccount, multiMonth, months, dailyMonth, setDailyMonth, selectCls }) {
  const monthIdx = months.indexOf(dailyMonth);
  const goPrev = () => { if (monthIdx > 0) setDailyMonth(months[monthIdx - 1]); };
  const goNext = () => { if (monthIdx < months.length - 1) setDailyMonth(months[monthIdx + 1]); };

  return (
    <>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <select value={dailyAccount} onChange={e => setDailyAccount(e.target.value)} className={selectCls} aria-label="Account">
          <option value="all">All accounts (total)</option>
          {ACCOUNT_KEYS.map(([k, lbl]) => <option key={k} value={k}>{lbl}</option>)}
        </select>
        {multiMonth && (
          <div className="inline-flex items-center gap-1">
            <button type="button" onClick={goPrev} disabled={monthIdx <= 0}
              className="h-9 w-9 flex items-center justify-center rounded-lg border border-divider text-muted hover:text-secondary disabled:opacity-40"><MdChevronLeft className="text-lg" /></button>
            <span className="text-sm font-medium text-secondary min-w-[110px] text-center">{bsMonthLabel(dailyMonth)}</span>
            <button type="button" onClick={goNext} disabled={monthIdx >= months.length - 1}
              className="h-9 w-9 flex items-center justify-center rounded-lg border border-divider text-muted hover:text-secondary disabled:opacity-40"><MdChevronRight className="text-lg" /></button>
          </div>
        )}
      </div>

      {multiMonth && (
        <p className="flex items-center gap-1.5 text-xs text-muted mb-3">
          <MdInfoOutline className="text-sm flex-shrink-0" />
          This range spans several months — the daily view pages by month. The account cards above cover the full range.
        </p>
      )}

      {rows.length === 0 ? (
        <div className="bg-card rounded-2xl border border-divider p-10 text-center text-muted text-sm">No days in this range.</div>
      ) : (
        <>
          {/* Mobile cards */}
          <div className="space-y-2 md:hidden">
            {rows.map((r) => (
              <div key={r.date} className="bg-card rounded-xl border border-divider p-3">
                <div className="flex items-center justify-between">
                  <DualDate date={parseLocal(r.date)} className="text-xs text-secondary font-medium" />
                  <span className={`text-sm font-bold ${r.closing == null ? 'text-muted' : 'text-secondary'}`}>{fmtBal(r.closing)}</span>
                </div>
                <div className="flex items-center gap-3 mt-1.5 text-[11px]">
                  <span className="text-muted">Open {fmtBal(r.opening)}</span>
                  <span className="text-green-600">+{fmtNPR(r.in)}</span>
                  <span className="text-red-600">−{fmtNPR(r.out)}</span>
                  {!r.available && <span className="text-muted">· unavailable before anchor</span>}
                </div>
              </div>
            ))}
          </div>

          {/* md+ table */}
          <div className="hidden md:block bg-card rounded-2xl border border-divider overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-muted border-b border-divider bg-nav-hover/30">
                    <th className="px-4 py-2 font-semibold">Date</th>
                    <th className="px-4 py-2 font-semibold text-right">Opening</th>
                    <th className="px-4 py-2 font-semibold text-right">Money In</th>
                    <th className="px-4 py-2 font-semibold text-right">Money Out</th>
                    <th className="px-4 py-2 font-semibold text-right">Closing</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.date} className={`border-b border-divider last:border-0 ${r.available ? '' : 'opacity-60'}`}>
                      <td className="px-4 py-2 whitespace-nowrap"><DualDate date={parseLocal(r.date)} className="text-xs" /></td>
                      <td className="px-4 py-2 text-right whitespace-nowrap text-muted">{fmtBal(r.opening)}</td>
                      <td className="px-4 py-2 text-right whitespace-nowrap font-medium text-green-600">{r.in ? fmtNPR(r.in) : ''}</td>
                      <td className="px-4 py-2 text-right whitespace-nowrap font-medium text-red-600">{r.out ? fmtNPR(r.out) : ''}</td>
                      <td className="px-4 py-2 text-right whitespace-nowrap font-semibold text-secondary">{fmtBal(r.closing)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </>
  );
}
